/**
 * XPBD "small-steps" structural solver. Position-based distance constraints stay
 * stable even with real steel stiffness (explicit springs would explode), and
 * the true axial force is read back from strain (N = EA/L·ΔL) so failure checks
 * use real tension-yield and Euler-buckling capacities from model.ts.
 *
 * One integrator both finds static equilibrium AND animates the collapse when
 * members fail — exactly the bridge-builder trick.
 */
import {
  type Structure,
  type Member,
  type Node,
  compressionCapacity,
  tensionCapacity,
  connectionCapacity,
} from "@/physics/model";
import { velocityPressure } from "@/physics/wind";
import {
  type WindState,
  gustSpeed,
} from "@/physics/gustModel";
import { windForce } from "@/game/appurtenances";
import { foundationUtilization, foundationFailureMode } from "@/physics/foundation";
import { connectionUtilization } from "@/physics/baseConnection";
import {
  type ServiceabilityRef,
  type ServiceabilityReport,
  buildServiceabilityRef,
  computeServiceability,
} from "@/physics/serviceability";

export interface SolverParams {
  gravity: number; // m/s^2
  windSpeed: number; // m/s (3-s gust) — the wind dial (the MEAN when gusting)
  windDir: number; // radians; horizontal direction the wind blows toward
  Cd: number; // drag coefficient for members
  substeps: number;
  velDamping: number; // global velocity damping (1/s) to settle the static solve
  groundY: number; // fallback flat collision plane height (m)
  /** Terrain height at (x,z); if set, debris rests on the real hill, not a flat plane. */
  groundHeight?: (x: number, z: number) => number;
  /**
   * Turbulence intensity Iu for the gust generator. 0 = perfectly steady wind
   * (bit-for-bit today's behaviour, the DEFAULT); ~0.165 = open-terrain gusty.
   */
  gustiness: number;
  /** Deterministic gust-trace seed (so replays reproduce the same wind). */
  windSeed: number;
  /**
   * Radial ice thickness on every member/guy (m). Ice adds dead weight (ρ≈900
   * kg/m³ glaze) AND widens the wind profile (drag area grows by 2·t), so wind
   * + ice is the deadliest real load case (~56% of tower collapses — see
   * docs/engineering/README + ENGINEERING.md §5). 0 = no ice (default).
   */
  iceThickness: number;
  /**
   * Temperature change ΔT (°C) from the neutral install temperature. Steel
   * contracts in the cold and expands in the heat (ΔL = α·L·ΔT), which raises or
   * relaxes guy tension and pre-strains members — cold + ice is a classic
   * guy-snapping case. 0 = neutral (default).
   */
  temperature: number;
  /**
   * Corrosion / aging: fraction of section (and hence capacity) lost to rust,
   * galvanizing depletion and section loss over the tower's life (doc 20). 0 = new
   * (default); 0.3 = a badly-neglected old tower down to 70% capacity. Applied as a
   * flat knock-down on member tension & buckling capacity in the failure check.
   */
  corrosion: number;
}

/** Glaze-ice density (kg/m³) — the dense, dangerous accretion. ISO 12494. */
const ICE_DENSITY = 900;
/** Thermal expansion coefficient of structural steel (per °C). */
const THERMAL_ALPHA = 12e-6;
/**
 * A member must stay over capacity for this many seconds before it actually fails.
 * Real steel buckles/yields over a finite time; a single-frame force spike (e.g. the
 * settling transient as guy pretension locks in, or a gust impulse) is not a failure.
 * Sustained overload persists well past this window, so genuine failures still trip
 * within ~a tenth of a second — visually instant — while transients are filtered.
 */
const FAIL_DEBOUNCE = 0.15;
/**
 * Once the tower is already coming down, overloads are dynamic and brief (the
 * structure whips, folds and slams the ground), so the steady-state debounce
 * filters out most of the secondary breakages and the collapse reads as a single
 * snap. During collapse we drop to a near-immediate debounce so members that are
 * genuinely torn/crushed on the way down fail too — a full progressive cascade.
 */
const COLLAPSE_DEBOUNCE = 0.02;
/** Fatigue (Miner) tuning — see evaluateMembers. EMA weight for the running mean
 *  force; slow amplitude decay so a burst of cycling is "remembered"; the fatigue
 *  limit (fraction of capacity below which life is effectively infinite); and the
 *  damage rate (game-accelerated: ~40 s of near-capacity cycling → crack-through,
 *  minutes for moderate ranges, never below the limit). */
const FATIGUE_MEAN_ALPHA = 0.02;
const FATIGUE_AMP_DECAY = 0.99;
const FATIGUE_LIMIT = 0.35;
const FATIGUE_RATE = 0.05;

/** Human explanation for a foundation/anchor failure (for the event log & replay). */
function foundationReason(
  kind: "footing" | "anchor",
  mode: "bearing" | "uplift" | "sliding" | "anchorage",
): { label: string; detail: string } {
  if (kind === "footing") {
    switch (mode) {
      case "uplift":
        return {
          label: "Windward footing uplift",
          detail:
            "The windward footing was pulled out of the ground — the overturning couple's uplift beat the footing's hold-down (its dead weight + anchor bolts). The tower rotates about the leeward base and topples.",
        };
      case "sliding":
        return {
          label: "Base sliding",
          detail:
            "The base slid off its footing — wind shear at the base exceeded the concrete-on-soil friction and passive resistance.",
        };
      case "anchorage":
        return {
          label: "Base anchorage tore out",
          detail:
            "The steel-to-concrete anchorage (anchor bolts / stub) tore out of the footing under the overturning uplift.",
        };
      default:
        return {
          label: "Soil bearing failure",
          detail:
            "The leeward footing punched into the soil — the concentrated download under overturning exceeded the soil's allowable bearing capacity. Softer soil or a smaller/shallower footing makes this worse.",
        };
    }
  }
  switch (mode) {
    case "sliding":
      return {
        label: "Guy anchor dragged",
        detail: "The guy anchor dragged sideways — the guy's horizontal pull exceeded the anchor's lateral resistance.",
      };
    case "anchorage":
      return {
        label: "Guy anchor connection failed",
        detail: "The guy hardware / anchor-rod connection let go before the buried block did.",
      };
    default:
      return {
        label: "Guy anchor pullout",
        detail:
          "A guy anchor (deadman) was pulled out of the ground — the guy tension's uplift exceeded the buried block's pullout resistance. Losing a guy sheds its load to the others and the mast folds.",
      };
  }
}
/**
 * Failures are armed once the tower is QUIESCENT — its fastest node is moving slower
 * than this (m/s) — rather than at a fixed clock time. A slim guyed mast rings for
 * longer than a stubby monopole as its guys come taut, and a 300 m tower sways slower
 * than a 30 m one; keying off actual motion lets each structure settle to true static
 * equilibrium before we start judging member forces, regardless of its size/dynamics.
 */
const SETTLE_SPEED_EPS = 0.03;
/** Hard cap on the settle window (s) in case a tower never fully goes quiet. */
const MAX_WARMUP = 5.0;

export const defaultParams: SolverParams = {
  gravity: 9.81,
  windSpeed: 0,
  windDir: 0,
  Cd: 1.2,
  substeps: 24,
  velDamping: 1.6,
  groundY: 0,
  gustiness: 0, // steady by default → no regression
  windSeed: 1,
  iceThickness: 0, // no ice by default
  temperature: 0, // neutral temperature by default
  corrosion: 0, // pristine steel by default
};

export class Solver {
  readonly structure: Structure;
  params: SolverParams;
  collapsed = false; // the tower came down → "Fall Over" failed
  partsLost = false; // at least one member has broken off → "Get Sued" failed
  maxUtil = 0;
  failedThisFrame: Member[] = [];
  /** Foundation/anchor failures this frame — for the event log & failure replay so
   *  the player sees WHY the base let go (bearing / uplift / sliding / anchorage). */
  foundationFailedThisFrame: Array<{
    kind: "footing" | "anchor";
    mode: "bearing" | "uplift" | "sliding" | "anchorage";
    label: string;
    detail: string;
    position: { x: number; y: number; z: number };
  }> = [];

  /** Worst foundation utilization this frame (0..≥1), read-only for the HUD. */
  private _maxFoundationUtil = 0;
  get maxFoundationUtil(): number {
    return this._maxFoundationUtil;
  }
  /** Worst steel↔concrete connection utilization this frame, read-only. */
  private _maxConnectionUtil = 0;
  get maxConnectionUtil(): number {
    return this._maxConnectionUtil;
  }
  /** Worst member Miner fatigue damage (0..≥1), read-only for the HUD. Persists
   *  across frames — fatigue accumulates, it isn't recomputed from scratch. */
  private _maxFatigue = 0;
  get maxFatigue(): number {
    return this._maxFatigue;
  }

  /**
   * Serviceability (top drift, twist/sway, microwave-link soft-fail) — an
   * OBSERVER of the solve, computed each step, never feeding back into it (doc
   * 25). Null until `initServiceability` is called with the render rings; the
   * headless harness and unit tests that don't wire it in are unaffected.
   */
  private serviceabilityRef: ServiceabilityRef | null = null;
  private _serviceability: ServiceabilityReport | null = null;
  /** Read-only serviceability report for the HUD (null if not initialised). */
  get serviceability(): ServiceabilityReport | null {
    return this._serviceability;
  }
  /**
   * Optional daily solar-bowing pointing wander (deg) added to each dish's
   * pointing error (doc 25 §5 / doc 14 §5). 0 = off (default) → link status is
   * pure wind/gravity, no behaviour change.
   */
  solarTiltDeg = 0;

  /**
   * Wire in serviceability tracking from the render centreline rings (node-index
   * rows bottom→top) captured at the UNDEFORMED build geometry. Call once, right
   * after construction, before stepping — the reference snapshot must be taken
   * before the tower moves. Optional: designs/harnesses that skip this simply get
   * a null `serviceability`.
   */
  initServiceability(rings: number[][], height: number): void {
    this.serviceabilityRef = buildServiceabilityRef(this.structure, rings, height);
    this._serviceability = computeServiceability(
      this.structure,
      this.serviceabilityRef,
      this.solarTiltDeg,
    );
  }

  /**
   * Instantaneous (gusting) wind speed used by the physics this frame. Derived
   * from params.windSpeed (the mean/dial) via the gust model, so ramping the
   * dial externally (as scripts do) is never corrupted by feedback.
   */
  private instWindSpeed = 0;

  /** Rest-length multiplier from thermal expansion (1 = neutral). Set each step. */
  private thermalFactor = 1;

  /**
   * Guy-pretension ramp (0 → 1 over the warm-up). Real masts are tensioned
   * gradually during erection; applying full pretension in one step yanks the
   * slim mast and spikes transient member forces that false-fail bracing. Ramping
   * eases the guys in so the structure settles smoothly to equilibrium.
   */
  private preRamp = 1;

  // Aggregate wind demand accumulated each step (noise-free, straight from the
  // load model) so the base-row foundation check uses clean whole-tower reactions
  // (base shear + overturning moment) instead of per-node XPBD residuals.
  private windShearX = 0;
  private windShearZ = 0;
  private windMoment = 0; // Σ F_wind_horizontal · height (N·m) about the base
  private totalWeight = 0; // Σ node mass · g (N) — net download on the base

  // per-node external force accumulators
  private fx: Float64Array;
  private fy: Float64Array;
  private fz: Float64Array;

  // A warm-up window lets the tower settle from its (unstressed) built geometry
  // under gravity + guy pretension WITHOUT allowing members to fail — otherwise
  // the one-step startup jolt spikes transient forces and false-fails members.
  private time = 0;
  private frameDt = 1 / 60;
  // Startup settle: guy pretension ramps in over [0, warmup]; member/foundation
  // failures stay disabled until the tower goes quiescent (see `failsArmed`) so the
  // one-time settling transient — guys tensioning, tower easing under gravity — never
  // false-fails a healthy member. `warmup` is just the pretension-ramp / minimum
  // settle; the actual arm point adapts to when the structure stops moving.
  private warmup = 1.2;
  private failsArmed = false; // latched true once quiescent (or MAX_WARMUP hit)
  private maxSpeed = 0; // fastest node this frame (m/s) — quiescence signal
  private initialMaxY: number;

  // Base-row foundation group: the fixed base nodes share one physical footing
  // (a pier/pad under the tower). Precomputed geometry so the base check uses a
  // clean whole-tower reaction (weight + wind shear + overturning couple).
  private baseNodeIdx: number[] = [];
  private baseRadius = 0; // max horizontal distance of a base node from centreline

  // Stiffness-adaptive substep floor for GUYED masts. Tension-only guys engage and
  // slacken abruptly and give the slim mast a high natural frequency; if the substep
  // h approaches the stiffest member's period T = 2π/√(k·(1/mᵢ+1/mⱼ)) the explicit
  // velocity update injects energy and the tower numerically diverges (a real ROHN
  // 45G blows up at the default 24 substeps but is rock-steady at ~48). Sized per
  // structure so a slim guyed mast gets the resolution it needs. Self-supporting
  // towers (no tension-only members) are stable at the default and are left alone —
  // their redundant bracing doesn't provoke this, so we don't pay for extra substeps.
  private substepFloor = 1;

  constructor(structure: Structure, params: Partial<SolverParams> = {}) {
    this.structure = structure;
    this.params = { ...defaultParams, ...params };
    const n = structure.nodes.length;
    this.fx = new Float64Array(n);
    this.fy = new Float64Array(n);
    this.fz = new Float64Array(n);
    this.initialMaxY = structure.nodes.reduce((m, nd) => Math.max(m, nd.y), 0);

    // gather the base-row footing group (non-anchor foundation nodes).
    let cx = 0;
    let cz = 0;
    for (let i = 0; i < structure.nodes.length; i++) {
      const nd = structure.nodes[i]!;
      if (nd.foundationCap && !nd.foundationCap.isAnchor) {
        this.baseNodeIdx.push(i);
        cx += nd.x;
        cz += nd.z;
      }
    }
    if (this.baseNodeIdx.length > 0) {
      cx /= this.baseNodeIdx.length;
      cz /= this.baseNodeIdx.length;
      for (const i of this.baseNodeIdx) {
        const nd = structure.nodes[i]!;
        this.baseRadius = Math.max(this.baseRadius, Math.hypot(nd.x - cx, nd.z - cz));
      }
    }

    // Guyed masts (tension-only members) get a stiffness-adaptive substep floor:
    // the slim mast + abruptly engaging/slackening guys give a high natural
    // frequency that diverges at the default 24 substeps. NOT applied to redundant
    // self-support/monopole lattices — their theoretical stiffest-member frequency
    // is huge (short dense-lattice belts) but they don't actually diverge, so the
    // floor would needlessly run them at up to 200 substeps (8× slower). ω² = k·(1/mᵢ+1/mⱼ).
    if (structure.members.some((m) => m.tensionOnly)) {
      let wmax = 0;
      for (const m of structure.members) {
        const im = structure.nodes[m.i]!.invMass + structure.nodes[m.j]!.invMass;
        const w2 = m.k * im; // ω²
        if (w2 > wmax) wmax = w2;
      }
      const omega = Math.sqrt(wmax);
      // substeps per stiffest-member period. Needs to comfortably exceed the
      // explicit-integrator energy-growth limit: empirically a guyed mast that
      // settles fine then blows up at 24 substeps is rock-steady at ~60 (e.g.
      // Bozrah 187 ft: 0 failures at 60+, 74 at 24). SAFE≈0.8 and a 200 cap give
      // slim/tall masts (Warsaw, KVLY) the resolution their high frequency needs.
      const SAFE = 0.8;
      this.substepFloor = Math.min(200, Math.max(1, Math.ceil(((1 / 60) * omega) / SAFE)));
    }
  }

  /** True during the startup settle — the window in which member/foundation
   *  failures are suppressed while the tower eases to equilibrium. Ends when the
   *  structure goes quiescent (failsArmed), not at a fixed time. */
  get settling(): boolean {
    return !this.failsArmed;
  }

  /** Advance the simulation by dt seconds. */
  step(dt: number): void {
    const p = this.params;
    const sub = Math.max(1, p.substeps, this.substepFloor);
    // Guard against a zero/degenerate frame (e.g. Clock.getDelta() returns 0 on
    // its first call) — h would be 0 and 1/h in updateVelocities poisons every
    // node with NaN. Skip until we have a real timestep. Cap at 1/60 s so a
    // throttled/large frame can't take a coarse step that destabilises sensitive
    // structures (guyed masts); the sim just runs in slow-motion under load.
    const frameDt = Math.min(dt, 1 / 60);
    if (frameDt <= 1e-6) return;
    this.frameDt = frameDt;
    this.time += frameDt;
    const h = frameDt / sub;

    // Modulate the dial (mean) into an instantaneous gusting speed. With
    // gustiness = 0 (default) gustSpeed() returns the mean exactly → steady wind,
    // identical to before. params.windSpeed (the dial) is never overwritten.
    const wind: WindState = {
      meanSpeed: p.windSpeed,
      gustiness: p.gustiness,
      seed: p.windSeed,
    };
    this.instWindSpeed = gustSpeed(wind, this.time);
    this.thermalFactor = 1 + THERMAL_ALPHA * p.temperature;
    // Ease guy pretension in over the warm-up with a smootherstep so it starts and
    // ends with zero slope (no jolt when it locks to full tension). After warm-up
    // preRamp = 1 → full installed pretension, identical to before.
    {
      const x = Math.min(1, this.time / this.warmup);
      this.preRamp = x * x * x * (x * (x * 6 - 15) + 10);
    }

    this.accumulateExternalForces();
    // Modestly extra velocity damping while settling so the tower rings down to
    // equilibrium quickly, then the light running value once armed. Kept MILD (not
    // near-critical): heavy damping suppresses velocity even mid-creep, which fools
    // the velocity-based quiescence test into arming before the structure has truly
    // settled. Stability of stiff guyed masts is handled by the adaptive substep
    // floor, not by over-damping here.
    const vd = this.settling ? p.velDamping * 4 : p.velDamping;
    const damp = Math.max(0, 1 - vd * h);

    for (let s = 0; s < sub; s++) {
      this.integrate(h, damp);
      this.solveConstraints(h);
      this.collide();
      this.updateVelocities(h);
    }

    // Arm failures once the tower has gone quiescent (or the safety cap is hit),
    // so the settling transient passes harmlessly. Only measured after the minimum
    // warm-up (pretension fully ramped). Latches — once live it stays live even if
    // a later collapse makes nodes move fast again.
    if (!this.failsArmed) {
      let maxSp2 = 0;
      for (const n of this.structure.nodes) {
        if (n.invMass === 0) continue;
        const sp2 = n.vx * n.vx + n.vy * n.vy + n.vz * n.vz;
        if (sp2 > maxSp2) maxSp2 = sp2;
      }
      this.maxSpeed = Math.sqrt(maxSp2);
      if (this.time >= this.warmup && (this.maxSpeed < SETTLE_SPEED_EPS || this.time >= MAX_WARMUP)) {
        this.failsArmed = true;
      }
    }

    this.evaluateMembers();

    // Serviceability observer (read-only): update top drift, twist/sway and the
    // microwave-link soft-fail from the freshly-solved positions (doc 25).
    if (this.serviceabilityRef) {
      this._serviceability = computeServiceability(
        this.structure,
        this.serviceabilityRef,
        this.solarTiltDeg,
      );
    }
  }

  private accumulateExternalForces(): void {
    const { nodes, members } = this.structure;
    const p = this.params;
    this.fx.fill(0);
    this.fy.fill(0);
    this.fz.fill(0);

    // gravity — also tally total weight (net base download, noise-free).
    let weight = 0;
    for (let i = 0; i < nodes.length; i++) {
      const w = nodes[i]!.mass * p.gravity;
      this.fy[i]! -= w;
      weight += w;
    }
    this.totalWeight = weight;
    // ice dead weight: a radial glaze layer on every member adds mass. Ice
    // cross-section (annulus) = π·t·(d + t) with d = member drag width; weight is
    // split to the two end nodes. Ice on guys is included (a classic real trigger).
    const t = p.iceThickness;
    if (t > 0) {
      for (const m of members) {
        if (m.failed) continue;
        const d = m.section.dragWidth;
        const iceArea = Math.PI * t * (d + t); // m²
        const wIce = iceArea * m.L0 * ICE_DENSITY * p.gravity; // N over the member
        this.fy[m.i]! -= wIce * 0.5;
        this.fy[m.j]! -= wIce * 0.5;
        this.totalWeight += wIce;
      }
      // ice on mounted appurtenances: a slab of ice over the frontal (EPA) area
      for (let i = 0; i < nodes.length; i++) {
        const ml = nodes[i]!.mountedLoads;
        if (!ml) continue;
        for (const { load } of ml) {
          const wIce = load.appurtenance.epa * t * ICE_DENSITY * p.gravity;
          this.fy[i]! -= wIce;
          this.totalWeight += wIce;
        }
      }
    }
    // reset aggregate wind demand for this step (used by the base-row foundation
    // check as a clean whole-tower reaction, not per-node XPBD residuals).
    this.windShearX = 0;
    this.windShearZ = 0;
    this.windMoment = 0;

    // wind drag on members (sail area = dragWidth × length, reduced by the
    // angle between the member and the wind). Uses the instantaneous (gusting)
    // speed so a gust that peaks past capacity fails a member in that instant.
    const V = this.instWindSpeed;
    if (V > 0) {
      const wx = Math.cos(p.windDir);
      const wz = Math.sin(p.windDir);
      for (const m of members) {
        if (m.failed) continue;
        const a = nodes[m.i]!;
        const b = nodes[m.j]!;
        let ax = b.x - a.x, ay = b.y - a.y, az = b.z - a.z;
        const len = Math.hypot(ax, ay, az) || 1;
        ax /= len; ay /= len; az /= len;
        const along = ax * wx + az * wz; // wind is horizontal
        const sinFactor = Math.sqrt(Math.max(0, 1 - along * along));
        const zmid = (a.y + b.y) * 0.5;
        const q = velocityPressure(V, zmid);
        // ice widens the wind profile by 2·t (bigger sail area — wind-on-ice)
        const area = (m.section.dragWidth + 2 * p.iceThickness) * m.L0 * sinFactor;
        const f = q * p.Cd * area * 0.5; // half to each node
        this.fx[m.i]! += wx * f; this.fz[m.i]! += wz * f;
        this.fx[m.j]! += wx * f; this.fz[m.j]! += wz * f;
        // aggregate whole-member wind (2·f) into base shear + overturning moment.
        const fTot = f * 2;
        this.windShearX += wx * fTot;
        this.windShearZ += wz * fTot;
        this.windMoment += fTot * Math.max(0, zmid);
      }

      // appurtenance loads: a height-scaled horizontal wind force per mounted
      // load (Kz + Gh already inside windForce), pushed along the wind axis.
      for (let i = 0; i < nodes.length; i++) {
        const ml = nodes[i]!.mountedLoads;
        if (!ml) continue;
        for (const { load } of ml) {
          const F = windForce(load, V);
          this.windShearX += wx * F;
          this.windShearZ += wz * F;
          this.windMoment += F * Math.max(0, load.height);
          this.fx[i]! += wx * F;
          this.fz[i]! += wz * F;
        }
      }
    }
  }

  private integrate(h: number, damp: number): void {
    const { nodes } = this.structure;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i]!;
      if (n.invMass === 0) continue;
      n.vx = (n.vx + n.invMass * this.fx[i]! * h) * damp;
      n.vy = (n.vy + n.invMass * this.fy[i]! * h) * damp;
      n.vz = (n.vz + n.invMass * this.fz[i]! * h) * damp;
      n.px = n.x; n.py = n.y; n.pz = n.z;
      n.x += n.vx * h; n.y += n.vy * h; n.z += n.vz * h;
    }
  }

  private solveConstraints(h: number): void {
    const { nodes, members } = this.structure;
    const h2 = h * h;
    for (const m of members) {
      if (m.failed) continue;
      const a = nodes[m.i]!;
      const b = nodes[m.j]!;
      let dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-9) continue;
      // thermal: cold shortens the rest length (→ tension), heat lengthens it.
      // pretension ramp: relax a pretensioned member's rest length toward its
      // installed (untensioned) length while preRamp < 1, easing the guy in.
      const rest = m.L0 * this.thermalFactor * (1 + m.pretension * (1 - this.preRamp));
      const C = len - rest;
      if (m.tensionOnly && C <= 0) continue; // slack cable: no push
      const wsum = a.invMass + b.invMass;
      if (wsum === 0) continue;
      dx /= len; dy /= len; dz /= len;
      // XPBD single iteration with compliance = 1/k
      const alpha = 1 / (m.k * h2);
      const s = -C / (wsum + alpha);
      if (a.invMass > 0) {
        a.x -= dx * s * a.invMass;
        a.y -= dy * s * a.invMass;
        a.z -= dz * s * a.invMass;
      }
      if (b.invMass > 0) {
        b.x += dx * s * b.invMass;
        b.y += dy * s * b.invMass;
        b.z += dz * s * b.invMass;
      }
    }
  }

  /** Per-node collision radius (m) = half the fattest incident member, lazily built. */
  private nodeRadius: Float64Array | null = null;
  private nodeRadii(): Float64Array {
    if (this.nodeRadius) return this.nodeRadius;
    const { nodes, members } = this.structure;
    const r = new Float64Array(nodes.length).fill(0.05);
    for (const m of members) {
      const hw = Math.max(0.05, m.section.dragWidth * 0.5);
      if (hw > r[m.i]!) r[m.i] = hw;
      if (hw > r[m.j]!) r[m.j] = hw;
    }
    this.nodeRadius = r;
    return r;
  }

  private collide(): void {
    const { nodes } = this.structure;
    const gh = this.params.groundHeight;
    const flat = this.params.groundY;
    const rad = this.nodeRadii();
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i]!;
      if (n.invMass === 0) continue;
      // Rest the member's SURFACE on the terrain (ground + its radius), not its
      // centreline — otherwise the lower half of every bar/debris mesh sinks below.
      const gy = (gh ? gh(n.x, n.z) : flat) + rad[i]!;
      if (n.y < gy) {
        n.y = gy;
        n.px = n.x; // friction: kill horizontal drift on contact
        n.pz = n.z;
      }
    }
    // Wreckage pile: once collapsed, push overlapping debris nodes apart so parts
    // stack instead of passing through each other. Gated on collapse (it would
    // fight the intact truss, and costs nothing until then). Spatial-hashed → O(n).
    if (this.collapsed) this.pileDebris(rad);
  }

  /** Spatial-hash node-node separation so fallen debris forms a pile. */
  private pileDebris(rad: Float64Array): void {
    const { nodes } = this.structure;
    const CELL = 1.0; // grid cell (m); comfortably ≥ any 2·radius
    const grid = new Map<number, number[]>();
    const key = (ix: number, iz: number) => (ix * 73856093) ^ (iz * 19349663);
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i]!;
      if (n.invMass === 0) continue;
      const k = key(Math.floor(n.x / CELL), Math.floor(n.z / CELL));
      let arr = grid.get(k);
      if (!arr) grid.set(k, (arr = []));
      arr.push(i);
    }
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i]!;
      if (a.invMass === 0) continue;
      const ix = Math.floor(a.x / CELL);
      const iz = Math.floor(a.z / CELL);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          const arr = grid.get(key(ix + dx, iz + dz));
          if (!arr) continue;
          for (const j of arr) {
            if (j <= i) continue;
            const b = nodes[j]!;
            if (b.invMass === 0) continue;
            let dxx = b.x - a.x;
            let dyy = b.y - a.y;
            let dzz = b.z - a.z;
            const min = rad[i]! + rad[j]!;
            const d2 = dxx * dxx + dyy * dyy + dzz * dzz;
            if (d2 >= min * min || d2 < 1e-9) continue;
            const d = Math.sqrt(d2);
            const push = ((min - d) * 0.5) / d;
            dxx *= push;
            dyy *= push;
            dzz *= push;
            a.x -= dxx; a.y -= dyy; a.z -= dzz;
            b.x += dxx; b.y += dyy; b.z += dzz;
          }
        }
      }
    }
  }

  private updateVelocities(h: number): void {
    const { nodes } = this.structure;
    const inv = 1 / h;
    for (const n of nodes) {
      if (n.invMass === 0) continue;
      n.vx = (n.x - n.px) * inv;
      n.vy = (n.y - n.py) * inv;
      n.vz = (n.z - n.pz) * inv;
    }
  }

  /** Read axial forces from strain, compute utilization, fail overloaded members. */
  private evaluateMembers(): void {
    const { nodes, members } = this.structure;
    this.maxUtil = 0;
    this._maxFoundationUtil = 0;
    this._maxConnectionUtil = 0;
    this.failedThisFrame.length = 0;
    this.foundationFailedThisFrame.length = 0;
    let failedCount = 0;
    const allowFailure = !this.settling; // no failures until settled

    for (const m of members) {
      if (m.failed) {
        failedCount++;
        continue;
      }
      const a = nodes[m.i]!;
      const b = nodes[m.j]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
      const rest = m.L0 * this.thermalFactor * (1 + m.pretension * (1 - this.preRamp));
      m.force = m.k * (len - rest); // tension +, compression -
      m.slack = m.tensionOnly && m.force <= 0;
      if (m.slack) m.force = 0;

      // Member body: tension yield (+) or compression buckling (−). Corrosion /
      // aging knocks capacity down by the lost section fraction (doc 20).
      const corr = 1 - this.params.corrosion;
      const cap = (m.force >= 0 ? tensionCapacity(m) : compressionCapacity(m)) * corr;
      const memberUtil = cap > 0 ? Math.abs(m.force) / cap : 0;
      // End connection: the bolted joint carries the same axial force. It can let
      // go BEFORE the member body yields/buckles (doc 17). For stock sections the
      // connection develops the member (capacity ≥ member), so this only bites on
      // under-detailed custom designs; guys return Infinity (no separate check).
      const connCap = connectionCapacity(m) * corr; // rusted bolts/plates lose capacity too
      const connUtil = connCap > 0 && Number.isFinite(connCap) ? Math.abs(m.force) / connCap : 0;
      // The member's reported util is the worse of body vs connection — whichever
      // governs is what will fail first.
      const connGoverns = connUtil > memberUtil;
      m.util = connGoverns ? connUtil : memberUtil;
      if (m.util > this.maxUtil) this.maxUtil = m.util;

      // Fatigue (Miner cyclic-damage, doc 19): track the force fluctuation about a
      // slow running mean; the stress RANGE relative to capacity drives a Basquin
      // S-N damage rate. Below the fatigue limit → no damage (high-cycle infinite
      // life), so steady wind never fatigues — only gust buffeting / VIV / sway.
      m.fMean += (m.force - m.fMean) * FATIGUE_MEAN_ALPHA;
      m.fAmp = Math.max(Math.abs(m.force - m.fMean), m.fAmp * FATIGUE_AMP_DECAY);
      // Fatigue is driven by turbulent BUFFETING — only accrue when gusts are on.
      // (Steady mean wind produces some sim sway too, but that isn't the real
      // fatigue load; real tower fatigue is gust + VIV cyclic stress.)
      if (allowFailure && cap > 0 && this.params.gustiness > 0) {
        // cyclic stress range as a fraction of capacity, capped so a collapse-moment
        // force swing can't instantly max the damage (keeps it an accrual mechanic).
        const r = Math.min(2, m.fAmp / cap);
        if (r > FATIGUE_LIMIT) m.fatigue += this.frameDt * FATIGUE_RATE * r * r * r;
      }
      if (m.fatigue > this._maxFatigue) this._maxFatigue = m.fatigue;

      // Debounced failure: accumulate time-over-capacity; only fail once the
      // overload has been sustained past FAIL_DEBOUNCE. A transient spike resets
      // the moment util drops back under 1, so it never fails a healthy member.
      // A member also fails once its Miner fatigue damage reaches 1 (crack-through).
      if (m.util >= 1) m.overStress += this.frameDt;
      else m.overStress = 0;
      // Near-immediate failure once collapsing → the fall shreds members (cascade).
      const debounce = this.collapsed ? COLLAPSE_DEBOUNCE : FAIL_DEBOUNCE;
      if (allowFailure && (m.overStress >= debounce || m.fatigue >= 1)) {
        m.failed = true;
        // fatigue crack vs connection shear-out vs member snap/buckle (doc 17/19).
        m.failMode =
          m.fatigue >= 1 ? "fatigue" : connGoverns ? "shear" : m.force >= 0 ? "snap" : "buckle";
        failedCount++;
        this.partsLost = true; // a part broke off → "Get Sued"
        this.failedThisFrame.push(m);
      }
    }

    // --- foundations & base connections (series: weaker link governs) -------
    // Only after warm-up settle (same gate as member failure). Each fixed base /
    // guy-anchor node with a foundationCap reads its reaction from the incident
    // members' freshly-computed axial forces, projected to vertical & horizontal,
    // plus the node's own dead weight. If foundation OR connection utilization
    // ≥ 1, the pinned node is RELEASED (fixed=false; invMass=1/mass) — reusing the
    // solver's existing invMass 0→1/mass transition. Uplift/overturn release →
    // collapse; a guy-anchor release also sets partsLost ("Get Sued").
    if (allowFailure) {
      this.evaluateFoundations();
    }

    // Collapse = the tower physically came down: its highest point has dropped
    // well below where it started, or a large fraction of members have broken.
    let curMaxY = 0;
    for (const n of nodes) if (n.y > curMaxY) curMaxY = n.y;
    const fell = this.initialMaxY > 1 && curMaxY < this.initialMaxY * 0.6;
    const shattered = members.length > 0 && failedCount / members.length > 0.3;
    if (allowFailure && (fell || shattered)) this.collapsed = true;
  }

  /**
   * Check foundations against geotech + connection capacity and RELEASE any that
   * are overloaded (fixed=false; invMass=1/mass — the solver's existing pinned→
   * free transition). foundation & connection compose in SERIES (weaker governs).
   *
   * Two reaction read-backs, each using the cleanest available signal:
   *  - BASE ROW: the fixed base nodes share ONE physical footing (a pier/pad).
   *    Its demand is the whole-tower reaction, taken noise-free from the load
   *    model: net download = total weight; base shear = Σ wind; overturning
   *    moment M drives windward UPLIFT T = M / baseWidth (docs/FOUNDATIONS.md §5).
   *    (Per-node XPBD residuals in a tightly-packed base are unreliable, so we do
   *    NOT use them for the base.) If it fails, the whole base row releases.
   *  - GUY ANCHORS: each is a single tension-only guy to its own deadman, so the
   *    incident-member projection is a clean, real per-anchor reaction.
   */
  private evaluateFoundations(): void {
    const { nodes, members } = this.structure;
    const g = this.params.gravity;

    // --- base-row footing (shared, whole-tower reaction) --------------------
    if (this.baseNodeIdx.length > 0) {
      const idx0 = this.baseNodeIdx[0]!;
      const cap = nodes[idx0]!.foundationCap;
      // only while the base is still pinned (any one node standing in for the row)
      if (cap && nodes[idx0]!.fixed) {
        const nLegs = this.baseNodeIdx.length;
        const shear = Math.hypot(this.windShearX, this.windShearZ);
        // overturning couple → windward-leg uplift / leeward download over the
        // full base width (2·baseRadius). Split the download evenly across legs
        // for the per-leg bearing capacity; the couple adds/removes at the edges.
        const width = Math.max(0.1, this.baseRadius * 2);
        const couple = this.windMoment / width; // N, ± at windward/leeward legs
        const downPerLeg = this.totalWeight / nLegs;
        // worst leeward leg: base share + couple (compression into soil).
        const verticalDown = downPerLeg + couple;
        // worst windward leg: couple minus its download share → net uplift if +.
        const verticalUp = Math.max(0, couple - downPerLeg);
        const shearPerLeg = shear / nLegs;

        const uFound = foundationUtilization(cap.capacity, verticalDown, shearPerLeg);
        const uConn = connectionUtilization(
          cap.connection,
          verticalUp,
          shearPerLeg,
          Math.max(0, verticalDown),
        );
        if (uFound > this._maxFoundationUtil) this._maxFoundationUtil = uFound;
        if (uConn > this._maxConnectionUtil) this._maxConnectionUtil = uConn;

        if (Math.max(uFound, uConn) >= 1) {
          const mode = uConn >= uFound ? "uplift" : foundationFailureMode(cap.capacity, verticalDown, shearPerLeg);
          const reason = foundationReason("footing", mode);
          const n0 = nodes[idx0]!;
          this.foundationFailedThisFrame.push({
            kind: "footing",
            mode,
            ...reason,
            position: { x: n0.x, y: n0.y, z: n0.z },
          });
          this.partsLost = true; // a footing let go → "Get Sued"
          this.collapsed = true; // the base foundation is gone → the tower is coming down
          // release the whole base row: the footing let go → the tower folds.
          for (const bi of this.baseNodeIdx) {
            const node = nodes[bi]!;
            node.fixed = false;
            const m = Math.max(node.mass, cap.capacity.selfWeight / (g * nLegs));
            node.mass = m;
            node.invMass = 1 / m;
          }
        }
      }
    }

    // --- guy anchors (each its own deadman; clean per-node reaction) ---------
    for (let idx = 0; idx < nodes.length; idx++) {
      const node = nodes[idx]!;
      const cap = node.foundationCap;
      if (!cap || !cap.isAnchor || !node.fixed) continue;

      // reaction = -(net member force on the anchor); a taut guy pulls the anchor
      // toward the mast (force · unit(other - node)).
      let sfx = 0;
      let sfy = 0;
      let sfz = 0;
      for (const m of members) {
        if (m.failed) continue;
        let other: Node;
        if (m.i === idx) other = nodes[m.j]!;
        else if (m.j === idx) other = nodes[m.i]!;
        else continue;
        let dx = other.x - node.x;
        let dy = other.y - node.y;
        let dz = other.z - node.z;
        const len = Math.hypot(dx, dy, dz) || 1;
        dx /= len; dy /= len; dz /= len;
        sfx += m.force * dx;
        sfy += m.force * dy;
        sfz += m.force * dz;
      }
      sfy -= node.mass * g; // anchor block dead weight presses down

      const verticalDown = -sfy;
      const verticalUp = Math.max(0, sfy); // guy pulling the anchor up → pullout
      const shear = Math.hypot(sfx, sfz);

      const uFound = foundationUtilization(cap.capacity, verticalDown, shear);
      const uConn = connectionUtilization(
        cap.connection,
        verticalUp,
        shear,
        Math.max(0, verticalDown),
      );
      if (uFound > this._maxFoundationUtil) this._maxFoundationUtil = uFound;
      if (uConn > this._maxConnectionUtil) this._maxConnectionUtil = uConn;

      if (Math.max(uFound, uConn) >= 1) {
        const mode = uConn >= uFound ? "anchorage" : foundationFailureMode(cap.capacity, verticalDown, shear);
        const reason = foundationReason("anchor", mode);
        this.foundationFailedThisFrame.push({
          kind: "anchor",
          mode,
          ...reason,
          position: { x: node.x, y: node.y, z: node.z },
        });
        node.fixed = false;
        const m = Math.max(node.mass, cap.capacity.selfWeight / g);
        node.mass = m;
        node.invMass = 1 / m;
        this.partsLost = true; // an anchor tore out → "Get Sued"
      }
    }
  }
}
